/**
 * v4.31.0 — Location Guest Routing & Review Platforms section.
 *
 * Per-location overrides for the review-destination URLs used at tap
 * time. Cascade (server-side):
 *     location.platform_urls[p].url  (enabled + non-empty)
 *     → platform_values[org,p].review_url   (legacy override)
 *     → organization.platform_urls[p]
 *     → DEFAULT_PLATFORM_URLS[p]
 *
 * Backward-compat properties (verified against backend code):
 *   - NFC chips store only `card_id`; URL is computed server-side at
 *     tap time. No reprogramming or QR regeneration required.
 *   - `db.taps` does not persist resolved URLs → tap-history and
 *     analytics are unaffected.
 *   - feedback_sessions don't store URLs → Review Shield history
 *     is unaffected.
 *   - When this section is empty/disabled the cascade falls through
 *     to the existing org-level URL exactly as before v4.31.0.
 *
 * UX requirements (from product spec):
 *   - Collapsible section inside the Location edit modal.
 *   - Per-platform: URL input, enable toggle, optional priority.
 *   - URL validation (http:// or https://).
 *   - Mobile / iPad / desktop friendly.
 *   - Sticky save footer parity → inline Save button + toast feedback
 *     mirroring the main form's pattern.
 *   - Trilingual EN / FR / ES via `t('locations.routing.…')`.
 */
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, Globe, Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from './ui/collapsible';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { locationsAPI } from '../lib/api';
import { stringifyDetail } from '../lib/utils';

// Whitelist must match `backend/routes/locations.py:ALLOWED_PLATFORM_KEYS`.
const PLATFORM_KEYS = [
    'google_reviews',
    'facebook',
    'tripadvisor',
    'yelp',
    'instagram',
    'tiktok',
    'trustpilot',
    'ratemds',
    'custom_1',
    'custom_2',
];

const URL_RE = /^https?:\/\/[^\s]+\.[^\s]+/i;

function isValidUrl(url) {
    if (!url) return true; // empty is allowed (means "fall through")
    return URL_RE.test(url.trim());
}

export default function LocationRoutingSection({ locationId }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [entries, setEntries] = useState({}); // { platform_key: { url, enabled, priority } }
    const [errors, setErrors] = useState({}); // { platform_key: i18n_msg }

    // Load existing per-location URL config when the section first opens.
    useEffect(() => {
        if (!open || !locationId) return;
        let cancelled = false;
        setLoading(true);
        locationsAPI
            .getPlatformUrls(locationId)
            .then((res) => {
                if (cancelled) return;
                const incoming = res?.data?.platform_urls || {};
                // Normalise so every entry has the {url, enabled, priority} shape.
                const norm = {};
                Object.entries(incoming).forEach(([k, v]) => {
                    if (!v || typeof v !== 'object') return;
                    norm[k] = {
                        url: v.url || '',
                        enabled: v.enabled !== false,
                        priority:
                            typeof v.priority === 'number' ? v.priority : null,
                    };
                });
                setEntries(norm);
            })
            .catch(() => {
                if (!cancelled) setEntries({});
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, locationId]);

    const usedKeys = useMemo(() => new Set(Object.keys(entries)), [entries]);
    const availableKeys = useMemo(
        () => PLATFORM_KEYS.filter((k) => !usedKeys.has(k)),
        [usedKeys]
    );

    const updateEntry = (key, patch) => {
        setEntries((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || { url: '', enabled: true, priority: null }), ...patch },
        }));
        setErrors((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const addPlatform = (key) => {
        if (!key || usedKeys.has(key)) return;
        setEntries((prev) => ({
            ...prev,
            [key]: { url: '', enabled: true, priority: null },
        }));
    };

    const removePlatform = (key) => {
        setEntries((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setErrors((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleSave = async () => {
        // Client-side validation pass.
        const nextErrors = {};
        for (const [key, entry] of Object.entries(entries)) {
            if (entry.url && !isValidUrl(entry.url)) {
                nextErrors[key] = t('locations.routing.invalid_url');
            }
        }
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            return;
        }
        setSaving(true);
        try {
            await locationsAPI.updatePlatformUrls(locationId, entries);
            toast.success(t('locations.routing.save_success'));
        } catch (err) {
            const detail = stringifyDetail(err?.response?.data?.detail) ||
                t('locations.routing.save_failed');
            toast.error(detail);
        } finally {
            setSaving(false);
        }
    };

    if (!locationId) return null;

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className="mt-6 border rounded-lg overflow-hidden border-slate-200 dark:border-slate-700"
            data-testid="location-routing-section"
        >
            <CollapsibleTrigger asChild>
                <button
                    type="button"
                    className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
                    data-testid="location-routing-toggle"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <Globe className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-400 flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                                {t('locations.routing.section_title')}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {Object.keys(entries).length > 0
                                    ? `${Object.keys(entries).length} configured`
                                    : t('locations.routing.fallback_note')}
                            </div>
                        </div>
                    </div>
                    {open ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                </button>
            </CollapsibleTrigger>

            <CollapsibleContent>
                <div className="p-4 space-y-4 bg-white dark:bg-slate-900">
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                        {t('locations.routing.section_help')}
                    </p>

                    {loading ? (
                        <div className="flex items-center justify-center py-6 text-slate-500" data-testid="location-routing-loading">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            <span className="text-xs">…</span>
                        </div>
                    ) : Object.keys(entries).length === 0 ? (
                        <div
                            className="text-xs text-slate-500 dark:text-slate-400 py-2"
                            data-testid="location-routing-empty"
                        >
                            {t('locations.routing.empty_state')}
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="location-routing-list">
                            {Object.entries(entries).map(([key, entry]) => (
                                <div
                                    key={key}
                                    className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/60 dark:bg-slate-800/40"
                                    data-testid={`location-routing-row-${key}`}
                                >
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <Label className="text-xs font-medium text-slate-700 dark:text-slate-200">
                                            {t(`locations.routing.platform.${key}`)}
                                        </Label>
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-1.5">
                                                <Switch
                                                    checked={!!entry.enabled}
                                                    onCheckedChange={(v) =>
                                                        updateEntry(key, { enabled: !!v })
                                                    }
                                                    data-testid={`location-routing-enable-${key}`}
                                                />
                                                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                                                    {t('locations.routing.enable_toggle')}
                                                </span>
                                            </div>
                                            <Button
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                className="w-7 h-7"
                                                onClick={() => removePlatform(key)}
                                                data-testid={`location-routing-remove-${key}`}
                                                aria-label="remove"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2">
                                        <div>
                                            <Input
                                                value={entry.url || ''}
                                                onChange={(e) =>
                                                    updateEntry(key, { url: e.target.value })
                                                }
                                                placeholder={t(
                                                    'locations.routing.url_placeholder'
                                                )}
                                                aria-invalid={!!errors[key]}
                                                data-testid={`location-routing-url-${key}`}
                                                className="h-9 text-sm"
                                            />
                                            {errors[key] && (
                                                <p
                                                    className="text-[11px] text-red-600 dark:text-red-400 mt-1"
                                                    data-testid={`location-routing-error-${key}`}
                                                >
                                                    {errors[key]}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <Input
                                                type="number"
                                                inputMode="numeric"
                                                value={
                                                    entry.priority === null ||
                                                    entry.priority === undefined
                                                        ? ''
                                                        : entry.priority
                                                }
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    updateEntry(key, {
                                                        priority:
                                                            v === ''
                                                                ? null
                                                                : Number.parseInt(v, 10),
                                                    });
                                                }}
                                                placeholder={t(
                                                    'locations.routing.priority_label'
                                                )}
                                                data-testid={`location-routing-priority-${key}`}
                                                className="h-9 text-sm"
                                                title={t('locations.routing.priority_help')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {availableKeys.length > 0 && (
                        <div className="flex items-center gap-2">
                            <Select onValueChange={(v) => addPlatform(v)} value="">
                                <SelectTrigger
                                    className="h-9 text-xs w-[220px]"
                                    data-testid="location-routing-add-trigger"
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" />
                                    <SelectValue
                                        placeholder={t('locations.routing.add_platform')}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableKeys.map((k) => (
                                        <SelectItem
                                            key={k}
                                            value={k}
                                            data-testid={`location-routing-add-option-${k}`}
                                        >
                                            {t(`locations.routing.platform.${k}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-900">
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || loading}
                            data-testid="location-routing-save"
                            size="sm"
                        >
                            {saving && (
                                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                            )}
                            {t('common.save')}
                        </Button>
                    </div>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}
